import { z } from 'zod';

import { documentCapabilitiesSchema } from '@/lib/document-capabilities';
import { DOCUMENT_STATUSES, type DocumentStatus } from '@/lib/document-status';

// Contratos del Editor y flujo validados con Zod (ENG-VAL-01): un drift del backend
// (enum fuera de unión, campo faltante) cae al estado de error de la query, no revienta
// en render. Los ids llegan como string (serializador de bigint).

export { DOCUMENT_STATUSES, type DocumentStatus };

const FLOW_ACTION_KINDS = [
  'transition',
  'return',
  'approve',
  // G2: conformidad del revisor asignado y cierre de la aprobación por Calidad.
  'review-conformity',
  'close-approval',
  'edit-online',
  'decommission',
  'start-modification',
] as const;
export type FlowActionKind = (typeof FLOW_ACTION_KINDS)[number];

// type_restricted: una regla del tipo de documento quita la acción (spec 2026-10-14 §8 D6).
const BLOCKED_REASONS = [
  'missing_permission',
  'type_restricted',
  'requires_change_log',
  'locked',
  // G2: la etapa la operan sus responsables asignados.
  'not_assigned',
  'quality_only',
  'org_unavailable',
  'pending_conformities',
  'already_conformed',
  'already_signed',
  'slot_covered',
  'no_sign_permission',
] as const;
export type BlockedReason = (typeof BLOCKED_REASONS)[number];

export const flowActionSchema = z.object({
  action: z.enum(FLOW_ACTION_KINDS),
  toStatus: z.enum(DOCUMENT_STATUSES).optional(),
  enabled: z.boolean(),
  blockedReason: z.enum(BLOCKED_REASONS).optional(),
  /** G2: Calidad actúa sobre una etapa con responsables asignados (intervención). */
  override: z.literal(true).optional(),
  /** G2: la acción exige motivo (devolución, avance forzado). */
  requiresReason: z.literal(true).optional(),
});
export type FlowAction = z.infer<typeof flowActionSchema>;

const RESPONSIBILITY_ROLES = ['elaborador', 'revisor', 'aprobador', 'capacitador'] as const;
export type ResponsibilityRole = (typeof RESPONSIBILITY_ROLES)[number];

/** G2-8: un cupo de la etapa abierta (persona o puesto) y su acto en la ronda. */
export const stageSlotSchema = z.object({
  key: z.string(),
  kind: z.enum(['user', 'position']),
  label: z.string(),
  status: z.enum(['done', 'pending']),
  actedBy: z.string().nullable(),
  actedAt: z.string().nullable(),
});
export type StageSlot = z.infer<typeof stageSlotSchema>;

/** G2-8: responsables de la etapa abierta. */
export const stageViewSchema = z.object({
  role: z.enum(RESPONSIBILITY_ROLES),
  round: z.number(),
  slots: z.array(stageSlotSchema),
  qualityActs: z.array(z.object({ actedBy: z.string(), actedAt: z.string() })),
  /** Aprobación sellada: la vista es el registro de lo firmado, sin pendientes. Un API
   *  anterior no la envía y la etapa se lee abierta. */
  sealed: z.boolean().default(false),
  /** Actos de la ronda (firmas o conformidades), no cupos: un acto puede cubrir varios. */
  actCount: z.number().int().nonnegative().default(0),
});
export type StageView = z.infer<typeof stageViewSchema>;

export const flowCapabilitiesSchema = z.object({
  entryId: z.string(),
  code: z.string(),
  flowInstanceId: z.string().nullable(),
  kind: z.enum(['creacion', 'modificacion']).nullable(),
  currentStatus: z.enum(DOCUMENT_STATUSES),
  targetVersion: z.number().nullable(),
  hasChangeLog: z.boolean(),
  approvedAt: z.string().nullable(),
  effectiveDate: z.string().nullable(),
  lockedAt: z.string().nullable(),
  availableActions: z.array(flowActionSchema),
  /** Editar en línea, imprimir y comentar sobre ESTE documento, con el motivo si no. */
  documentCapabilities: documentCapabilitiesSchema,
  /** G2: cómo actúa el lector en la etapa abierta, o null si no la opera. */
  actingAs: z.enum(['assignee', 'quality']).nullable(),
  /** G2: responsables de la etapa abierta, o null fuera de las etapas de trabajo. */
  stage: stageViewSchema.nullable(),
  /** G2: vigencia que fijó la primera firma de la ronda (las demás la repiten). */
  roundEffectiveDate: z.string().nullable(),
});
export type FlowCapabilities = z.infer<typeof flowCapabilitiesSchema>;

export const flowCapabilitiesResponseSchema = z.object({
  success: z.literal(true),
  data: flowCapabilitiesSchema,
});

export const flowHistoryItemSchema = z.object({
  id: z.string(),
  fromStatus: z.enum(DOCUMENT_STATUSES).nullable(),
  toStatus: z.enum(DOCUMENT_STATUSES),
  actorUserId: z.string().nullable(),
  reason: z.string().nullable(),
  isReproceso: z.boolean(),
  attributedRole: z.enum(RESPONSIBILITY_ROLES).nullable(),
  transitionedAt: z.string(),
});
export type FlowHistoryItem = z.infer<typeof flowHistoryItemSchema>;

export const flowHistoryResponseSchema = z.object({
  success: z.literal(true),
  data: z.array(flowHistoryItemSchema),
});

// Edición en línea (Opción A): vista incrustada + edición pop-out + captura. `kind`
// distingue el borrador en edición (solo para quien participa del ciclo) de la versión
// vigente (spec 2026-10-20 §6.6).
export const ONLINE_VIEW_KINDS = ['working', 'official'] as const;
export const onlineViewSchema = z.object({
  entryId: z.string(),
  code: z.string(),
  kind: z.enum(ONLINE_VIEW_KINDS),
  version: z.number().int().positive(),
  embedUrl: z.string(),
  expiresAt: z.string().nullable(),
});
export const onlineViewResponseSchema = z.object({
  success: z.literal(true),
  data: onlineViewSchema,
});
export type OnlineView = z.infer<typeof onlineViewSchema>;

export const onlineEditSchema = z.object({
  entryId: z.string(),
  code: z.string(),
  flowInstanceId: z.string(),
  status: z.enum(DOCUMENT_STATUSES),
  editUrl: z.string(),
  expiresAt: z.string().nullable(),
});
export const onlineEditResponseSchema = z.object({
  success: z.literal(true),
  data: onlineEditSchema,
});
export type OnlineEdit = z.infer<typeof onlineEditSchema>;

export const onlineCaptureSchema = z.object({
  entryId: z.string(),
  flowInstanceId: z.string(),
  versionLabel: z.string(),
  modifiedAt: z.string().nullable(),
});
export const onlineCaptureResponseSchema = z.object({
  success: z.literal(true),
  data: onlineCaptureSchema,
});
export type OnlineCapture = z.infer<typeof onlineCaptureSchema>;

export const onlineTakeLmsSchema = z.object({
  entryId: z.string(),
  lmsVersion: z.number().int().positive(),
});
export const onlineTakeLmsResponseSchema = z.object({
  success: z.literal(true),
  data: onlineTakeLmsSchema,
});
export type OnlineTakeLms = z.infer<typeof onlineTakeLmsSchema>;

// Comentarios (control de cambios).
export const COMMENT_STATUSES = ['open', 'addressed', 'wont_fix'] as const;
export type CommentStatus = (typeof COMMENT_STATUSES)[number];

export const commentSchema = z.object({
  id: z.string(),
  version: z.number(),
  authorUserId: z.string(),
  body: z.string(),
  sectionHint: z.string().nullable(),
  status: z.enum(COMMENT_STATUSES),
  resolvedById: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type Comment = z.infer<typeof commentSchema>;

export const commentsResponseSchema = z.object({
  success: z.literal(true),
  data: z.array(commentSchema),
});
export const commentResponseSchema = z.object({ success: z.literal(true), data: commentSchema });

export const STATUS_LABELS: Record<DocumentStatus, string> = {
  reservado: 'Reservado',
  borrador: 'Borrador',
  en_revision: 'En revisión',
  en_aprobacion: 'En aprobación',
  vigente: 'Vigente',
  obsoleto: 'Obsoleto',
  anulado: 'Anulado',
};

/** Clase de badge (chrome.css) por estado, para la cabecera del editor. */
export const STATUS_BADGE: Record<DocumentStatus, string> = {
  reservado: 'b-mut',
  borrador: 'b-mut',
  en_revision: 'b-warn',
  en_aprobacion: 'b-info',
  vigente: 'b-ok',
  obsoleto: 'b-red',
  anulado: 'b-mut',
};
